// "Bevries als ijkpunt" (Spoor B, sessie B2; endpoint uit B1).
//
// Een ijkpunt is een naam op een meetronde: "ijkpunt oud, 2 september". Zonder
// zo'n naam is de oude stand na een paar rondes niet meer terug te vinden —
// runs heten `meterkast-20260911-161424`, en daar kies je er over een maand
// geen goede uit. Mét een naam is de benchmark later na te lopen.
//
// Twee dingen die de server doet en dit onderdeel dus niet:
//  · welke run bevroren wordt als je er geen kiest (de nieuwste automatische);
//  · hoeveel rijen het label daarna draagt — dat getal komt uit het antwoord,
//    het wordt hier niet geteld.
//
// Eén label mag meerdere runs dragen (B1 §11): de meterkast en Spoor A kunnen
// allebei onder "ijkpunt oud" staan. Daarom geen waarschuwing bij een
// bestaande naam — hergebruik is bedoeld gedrag, niet een vergissing.

import { useState } from "react";

import { Card } from "../../components/ui";
import { api } from "../../lib/api";
import type { MeterkastResponse } from "../../contracts";
import { color, radius, size, weight } from "../../tokens";
import { datum } from "./BronnenStukken";
import { invoer, knopDonker, knopLicht } from "./stijl";
import { leesbaar } from "./woorden";

export function Ijkpunten({
  kast,
  onGewijzigd,
}: {
  kast: MeterkastResponse;
  onGewijzigd: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [naam, setNaam] = useState("");
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [gelukt, setGelukt] = useState<string | null>(null);

  async function bevries() {
    setBezig(true);
    setFout(null);
    setGelukt(null);
    try {
      const uit = await api.meterkastIjkpunt({ label: naam.trim() });
      setGelukt(
        `"${uit.label}" staat nu op run ${uit.runId} — ${uit.rijen} ${
          uit.rijen === 1 ? "meting" : "metingen"
        }.`,
      );
      setNaam("");
      setOpen(false);
      onGewijzigd();
    } catch (err) {
      // `leesbaar` en niet `err.reason`: bij een 404 maakt reason er "Niet
      // gevonden." van, terwijl de server juist een bruikbare zin stuurt
      // ("Er is nog geen meting om als ijkpunt vast te leggen.").
      setFout(leesbaar(err, "Het ijkpunt kon niet worden vastgelegd."));
    } finally {
      setBezig(false);
    }
  }

  return (
    <Card style={{ padding: "14px 18px" }}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px 16px",
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
          <span style={{ ...kopje, marginRight: 2 }}>IJkpunten</span>
          {kast.ijkpunten.length === 0 ? (
            <span style={{ fontSize: size.meta, color: color.faint }}>
              nog geen enkele meetronde heeft een naam
            </span>
          ) : (
            kast.ijkpunten.map((i) => (
              <span
                key={`${i.label}-${i.runId}`}
                data-ijkpunt={i.runId}
                title={`run ${i.runId} · methode ${i.methode} · periode ${i.period} · ${
                  datum(i.gemetenOp) ?? "datum onbekend"
                } · ${i.rijen} metingen`}
                style={{
                  background: color.lineFaint,
                  color: color.body,
                  borderRadius: radius.pill,
                  padding: "3px 10px",
                  fontSize: size.label,
                }}
              >
                {i.label}
              </span>
            ))
          )}
        </div>

        {!open && (
          <button type="button" style={knopLicht} onClick={() => setOpen(true)}>
            Bevries als ijkpunt
          </button>
        )}
      </div>

      {open && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (naam.trim().length >= 3) void bevries();
          }}
          style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}
        >
          <label style={{ flex: 1, minWidth: 240 }}>
            <span style={voorlezen}>Naam van het ijkpunt</span>
            <input
              type="text"
              value={naam}
              autoFocus
              maxLength={120}
              placeholder='bijvoorbeeld: ijkpunt oud, 2 september'
              onChange={(e) => setNaam(e.target.value)}
              style={invoer}
            />
          </label>
          <button
            type="submit"
            disabled={bezig || naam.trim().length < 3}
            style={knopDonker(bezig || naam.trim().length < 3)}
          >
            {bezig ? "Bezig…" : "Vastleggen"}
          </button>
          <button type="button" style={knopLicht} disabled={bezig} onClick={() => setOpen(false)}>
            Annuleren
          </button>
        </form>
      )}

      {open && (
        <p style={{ margin: "8px 0 0", fontSize: size.label, color: color.faint, lineHeight: 1.6 }}>
          De nieuwste automatische meetronde krijgt deze naam. Dezelfde naam mag op meer
          ronden staan — zo hoort bijvoorbeeld de oude weging overal onder één noemer.
        </p>
      )}

      {gelukt && (
        <p
          data-ijkpunt-melding="goed"
          style={{ margin: "8px 0 0", fontSize: size.meta, color: color.accentGreen }}
        >
          {gelukt}
        </p>
      )}
      {fout && (
        <p
          role="alert"
          data-ijkpunt-melding="fout"
          style={{ margin: "8px 0 0", fontSize: size.meta, color: color.accentRed }}
        >
          {fout}
        </p>
      )}
    </Card>
  );
}

const kopje = {
  fontSize: size.label,
  fontWeight: weight.medium,
  color: color.faint,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
} as const;

/** Alleen voor een schermlezer. */
const voorlezen = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;
